import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  Select,
  Tag,
  //   Tooltip,
  //   Space,
  Modal,
  Descriptions,
  message,
  Spin,
  Drawer,
} from "antd";
import Table from "../../../../core/pagination/datatable";
import CommonFooter from "../../../../core/common/footer/commonFooter";
import dayjs from "dayjs";
import SendMessageDrawer from "./SendMessageDrawer";
import axios from "axios";
import { BASE_CRM_URL } from "../../../components/config/config";

const MessageHub = () => {
  const [messages, setMessages] = useState([]);
  const [counts, setCounts] = useState({
    total: 0,
    sent: 0,
    failed: 0,
    pending: 0,
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [channelFilter, setChannelFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [retryingId, setRetryingId] = useState(null);

  const [loading, setLoading] = useState(false);

  const fetchMessageHistory = async () => {
    setLoading(true);

    try {
      const response = await axios.get(
        `${BASE_CRM_URL}notifications/GetMessageHistory`,
      );

      if (response.data?.Status) {
        const formattedData = (response.data.ResultData || []).map((item) => ({
          ...item,
          key: item.id,
          recipientName:
            item.recipientName || item.contactName || item.recipient || "N/A",
          relType: (item.recipientType || "").toLowerCase(),
          templateName: item.template || "N/A",
          createdAt: item.date || item.sentAt || null,
          retryCount: item.retryCount ?? 0,
          subject: item.subject || "",
        }));

        setMessages(formattedData);
        setCounts(
          response.data.Counts || {
            total: response.data.TotalRecords || 0,
            sent: 0,
            failed: 0,
            pending: 0,
          },
        );
      } else {
        setMessages([]);
        setCounts({ total: 0, sent: 0, failed: 0, pending: 0 });
      }
    } catch (error) {
      console.error("Error fetching message history:", error);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessageHistory();
  }, []);

  const filteredMessages = useMemo(() => {
    return messages.filter((item) => {
      const search = searchText.toLowerCase();

      const matchesSearch =
        item.recipientName.toLowerCase().includes(search) ||
        item.templateName.toLowerCase().includes(search) ||
        item.email.toLowerCase().includes(search) ||
        item.phone.includes(search) ||
        String(item.relId).includes(search);

      const matchesStatus =
        statusFilter === "all" ||
        item.status.toLowerCase() === statusFilter.toLowerCase();

      const matchesChannel =
        channelFilter === "all" ||
        item.channel.toLowerCase() === channelFilter.toLowerCase();

      const matchesType =
        typeFilter === "all" ||
        item.relType.toLowerCase() === typeFilter.toLowerCase();

      return matchesSearch && matchesStatus && matchesChannel && matchesType;
    });
  }, [messages, searchText, statusFilter, channelFilter, typeFilter]);

  const getStatusTag = (status) => {
    const colors = {
      Sent: "success",
      Failed: "error",
      Pending: "warning",
      Sending: "processing",
    };

    return <Tag color={colors[status] || "default"}>{status}</Tag>;
  };

  const handleAddMessages = (formData) => {
    const now = dayjs().format("YYYY-MM-DD HH:mm:ss");
    const apiResults = formData.apiResult?.results || {};
    const newRecords = [];

    const createRecord = (channel, result, templateName, subject, body) => {
      const sent = result?.status === true;

      const errorText =
        typeof result?.error === "string"
          ? result.error
          : result?.error
            ? JSON.stringify(result.error)
            : sent
              ? ""
              : "No successful send result was returned by the API.";

      return {
        id: `${Date.now()}-${channel}`,
        relType: formData.relType,
        relId: formData.relId,
        recipientName: formData.recipientName,
        email: formData.email || "",
        phone: formData.mobileNumber || formData.phone || "",
        channel,
        templateName,
        subject: subject || "",
        body: body || "",
        status: sent ? "Sent" : "Failed",
        createdAt: now,
        sentAt: sent ? now : null,
        retryCount: 0,
        error: errorText,
      };
    };

    if (formData.sendEmail) {
      newRecords.push(
        createRecord(
          "Email",
          apiResults.email,
          formData.emailTemplateName,
          formData.emailSubject,
          formData.emailBody,
        ),
      );
    }

    if (formData.sendWhatsApp) {
      newRecords.push(
        createRecord(
          "WhatsApp",
          apiResults.whatsapp,
          formData.whatsAppTemplateName,
          "",
          formData.whatsAppBody,
        ),
      );
    }

    setMessages((prev) => [...newRecords, ...prev]);
    setDrawerOpen(false);

    const sentCount = newRecords.filter(
      (item) => item.status === "Sent",
    ).length;

    const failedCount = newRecords.length - sentCount;

    if (failedCount === 0 && newRecords.length > 0) {
      message.success("Message(s) sent successfully.");
    } else if (sentCount > 0) {
      message.warning(`${sentCount} message(s) sent; ${failedCount} failed.`);
    } else {
      message.error(
        "Message sending failed. Check the error details in the table.",
      );
    }
  };

  // UI-only retry demonstration. Replace with your retry API later.
  const handleRetry = (record) => {
    if (record.status !== "Failed") return;

    Modal.confirm({
      title: "Retry message?",
      content: `Retry ${record.channel} for ${record.recipientName}? This is currently a UI demonstration and will not contact the provider.`,
      okText: "Retry",
      cancelText: "Cancel",
      onOk: () => {
        setRetryingId(record.id);

        setMessages((prev) =>
          prev.map((item) =>
            item.id === record.id
              ? {
                  ...item,
                  status: "Pending",
                  retryCount: item.retryCount + 1,
                  error: "",
                }
              : item,
          ),
        );

        setRetryingId(null);
        message.success("Message status changed to Pending (demo only).");
      },
    });
  };

  const columns = [
    {
      title: "Recipient",
      key: "recipient",
      width: 220,
      sorter: (a, b) => a.recipientName.localeCompare(b.recipientName),
      render: (_, record) => (
        <div className="d-flex align-items-center">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center me-2"
            style={{
              width: 38,
              height: 38,
              minWidth: 38,
              background: "#EAF4FF",
              color: "#1B2850",
            }}
          >
            <i
              className={`fa-solid ${
                record.relType === "lead" ? "fa-user-plus" : "fa-user-tie"
              }`}
            />
          </div>

          <div className="min-w-0">
            <div className="fw-semibold text-dark">{record.recipientName}</div>
            <small className="text-muted">
              {record.relType === "lead" ? "Lead" : "Customer"}
            </small>
          </div>
        </div>
      ),
    },
    {
      title: "Channel",
      dataIndex: "channel",
      key: "channel",
      width: 115,
      render: (channel) => (
        <Tag
          color={channel === "Email" ? "blue" : "green"}
          icon={
            <i
              className={`fa-solid ${
                channel === "Email" ? "fa-envelope" : "fa-comment-dots"
              } me-1`}
            />
          }
        >
          {channel}
        </Tag>
      ),
    },
    {
      title: "Template",
      dataIndex: "templateName",
      key: "templateName",
      sorter: (a, b) => a.templateName.localeCompare(b.templateName),
      render: (value, record) => (
        <div>
          <div className="fw-medium">{value}</div>
          {record.subject && (
            <small className="text-muted">{record.subject}</small>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: getStatusTag,
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 155,
      sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
      render: (value) =>
        value ? dayjs(value).format("DD-MM-YYYY hh:mm A") : "-",
    },
    {
      title: "Retries",
      dataIndex: "retryCount",
      key: "retryCount",
      width: 75,
      align: "center",
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      align: "center",
      render: (_, record) => (
        // <Space size="small">
        //   <Tooltip title="View message details">
        //     <Button
        //       type="text"
        //       icon={<i className="fa-regular fa-eye" />}
        //       onClick={() => {
        //         setSelectedMessage(record);
        //         setViewOpen(true);
        //       }}
        //     />
        //   </Tooltip>
        <>
          <div className="d-flex align-items-center gap-1">
            <div className="action-table-data">
              <div className="edit-delete-action">
                <Link
                  className="me-2 p-2 shadow-sm"
                  onClick={() => {
                    setSelectedMessage(record);
                    setViewOpen(true);
                  }}
                >
                  <i className="ti ti-eye text-info"></i>
                </Link>
              </div>
            </div>
            {record.status === "Failed" && (
              <div className="action-table-data">
                <div className="edit-delete-action">
                  <Link
                    className="me-2 p-2 shadow-sm"
                    loading={retryingId === record.id}
                    onClick={() => handleRetry(record)}
                  >
                    <i className="fa-solid fa-rotate-right text-danger"></i>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page header */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
          <div>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-1">
                <li className="breadcrumb-item">
                  <Link to="/crm/dashboard">
                    <i className="fas fa-home fa-lg" />
                  </Link>
                </li>
                <li className="breadcrumb-item " aria-current="page">
                  Message Hub
                </li>
              </ol>
            </nav>

            <small className="text-muted">
              View and manage email and WhatsApp messages
            </small>
          </div>

          <Button
            type="primary"
            size="medium"
            icon={<i className="fa-solid fa-paper-plane" />}
            onClick={() => setDrawerOpen(true)}
            style={{
              backgroundColor: "#1B2850",
              borderColor: "#1B2850",
            }}
          ></Button>
        </div>

        {/* Summary cards */}
        <div className="row g-3 mb-4">
          {[
            {
              title: "Total ",
              value: counts.total,
              icon: "fa-envelope",
              color: "#1B2850",
              bg: "#EAF4FF",
            },
            {
              title: "Sent",
              value: counts.sent,
              icon: "fa-circle-check",
              color: "#198754",
              bg: "#E7F7EF",
            },
            {
              title: "Failed",
              value: counts.failed,
              icon: "fa-circle-exclamation",
              color: "#DC3545",
              bg: "#FDEBEC",
            },
            {
              title: "Pending",
              value: counts.pending,
              icon: "fa-clock",
              color: "#D97706",
              bg: "#FFF4DF",
            },
          ].map((item) => (
            <div className="col-6 col-md-3" key={item.title}>
              <div className="card h-100 mb-0">
                <div className="card-body d-flex align-items-center p-3">
                  <div
                    className="rounded-3 d-flex align-items-center justify-content-center me-3"
                    style={{
                      width: 36,
                      height: 36,
                      background: item.bg,
                      color: item.color,
                      flexShrink: 0,
                    }}
                  >
                    <i className={`fa-solid ${item.icon} fs-4`} />
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    <span>{item.title}</span>
                    <span className="fw-bold fs-4">{item.value}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Message list */}
        <div className="card table-list-card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <div className="search-set p-3"></div>
            <div className="d-flex table-dropdown my-xl-auto">
              <div className="dropdown me-2">
                <Select
                  value={typeFilter}
                  onChange={setTypeFilter}
                  style={{ width: 135, height: 38 }}
                  options={[
                    { label: "All Types", value: "all" },
                    { label: "Leads", value: "lead" },
                    { label: "Customers", value: "customer" },
                  ]}
                />
              </div>
              <div className="dropdown me-2">
                <Select
                  value={channelFilter}
                  onChange={setChannelFilter}
                  style={{ width: 145, height: 38 }}
                  options={[
                    { label: "All Channels", value: "all" },
                    { label: "Email", value: "email" },
                    { label: "WhatsApp", value: "whatsapp" },
                  ]}
                />
              </div>
              <div className="dropdown me-2">
                <Select
                  value={statusFilter}
                  onChange={setStatusFilter}
                  style={{ width: 145, height: 38 }}
                  options={[
                    { label: "All Statuses", value: "all" },
                    { label: "Sent", value: "sent" },
                    { label: "Failed", value: "failed" },
                    { label: "Pending", value: "pending" },
                    { label: "Sending", value: "sending" },
                  ]}
                />
              </div>
              <Button
                onClick={() => {
                  setSearchText("");
                  setTypeFilter("all");
                  setChannelFilter("all");
                  setStatusFilter("all");
                }}
                style={{ height: 38, width: 40 }}
              >
                <i className="fa-solid fa-filter-circle-xmark" />
              </Button>
            </div>
          </div>

          <div className="card-body">
            <div className="table-responsive">
              {loading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "250px", // adjust height if needed
                  }}
                >
                  <Spin size="large" />
                </div>
              ) : (
                <Table
                  key={`${typeFilter}-${channelFilter}-${statusFilter}`}
                  columns={columns}
                  dataSource={filteredMessages}
                  rowKey="id"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <CommonFooter />

      {/* Separate send message drawer */}
      <SendMessageDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={handleAddMessages}
      />

      {/* View message details */}
      <Drawer
        title="Message Details"
        open={viewOpen}
        onClose={() => {
          setViewOpen(false);
          setSelectedMessage(null);
        }}
        placement="right"
        width={650}
        closable={true}
        destroyOnClose
        footer={
          <div className="d-flex justify-content-end">
            <Button
              onClick={() => {
                setViewOpen(false);
                setSelectedMessage(null);
              }}
            >
              Close
            </Button>
          </div>
        }
      >
        {selectedMessage && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Recipient">
              {selectedMessage.recipientName || "-"}
            </Descriptions.Item>

            <Descriptions.Item label="Type">
              {selectedMessage.relType || selectedMessage.recipientType || "-"}
              {" #"}
              {selectedMessage.relId || selectedMessage.recipientId || "-"}
            </Descriptions.Item>

            <Descriptions.Item label="Contact">
              {selectedMessage.recipient || "-"}
            </Descriptions.Item>

            <Descriptions.Item label="Channel">
              {selectedMessage.channel || "-"}
            </Descriptions.Item>

            <Descriptions.Item label="Template">
              {selectedMessage.templateName || selectedMessage.template || "-"}
            </Descriptions.Item>

            <Descriptions.Item label="Subject">
              {selectedMessage.subject || "-"}
            </Descriptions.Item>

            <Descriptions.Item label="Status">
              {getStatusTag(selectedMessage.status)}
            </Descriptions.Item>

            <Descriptions.Item label="Created At">
              {selectedMessage.createdAt
                ? dayjs(selectedMessage.createdAt).format("DD-MM-YYYY hh:mm A")
                : selectedMessage.date
                  ? dayjs(selectedMessage.date).format("DD-MM-YYYY hh:mm A")
                  : "-"}
            </Descriptions.Item>

            <Descriptions.Item label="Message">
              <div
                style={{
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                  maxHeight: 300,
                  overflowY: "auto",
                }}
              >
                {selectedMessage.messageBody
                  ? typeof selectedMessage.messageBody === "string"
                    ? selectedMessage.messageBody
                        .split("\n")
                        .map((line) => line.trim())
                        .join("\n")
                        .trim()
                    : JSON.stringify(selectedMessage.messageBody, null, 2)
                  : "-"}
              </div>
            </Descriptions.Item>

            {selectedMessage.error && (
              <Descriptions.Item label="Error">
                <span className="text-danger">{selectedMessage.error}</span>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Drawer>
    </div>
  );
};

export default MessageHub;
